import qs from 'qs';

import { client } from '@api/client';

import {
	IConsistencyCheckExtraPathDetails,
	IConsistencyCheckHistory,
	IConsistencyCheckHistoryDetailResponse,
	IConsistencyCheckHistoryInfoSummary,
	IConsistencyCheckHistoryStatistic,
	IConsistencyCheckHistorySummary,
	TConsistencyCheckHistoryInfo,
} from '@interfaces/cmModule/consistencyCheck';
import {
	IConsistencyCheckHistoryDetailsDTO,
	IConsistencyCheckHistoryExtraPathMoNamesDTO,
	TConsistencyCheckHistoryDTO,
	TConsistencyCheckHistoryExportDTO,
	TConsistencyCheckHistoryExtraPathMoIdsDTO,
	TConsistencyCheckHistoryExtraPathMoNamesDTO,
	TConsistencyCheckHistoryManagedIds,
	TConsistencyCheckHistoryStatisticsDTO,
	TDumpConsistencyIdAndPath,
} from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { IId, TIdName, TNameTitle } from '@interfaces/common/common';
import { IItemsResponse } from '@interfaces/common/response';

import { URL } from './urls';

const {
	configurationConsistencyAWTGRan,
	configurationConsistencyParametersAWTGRan,
} = URL;

export const getConsistencyCheckHistoryHistory = (
	params: TConsistencyCheckHistoryDTO,
): Promise<IItemsResponse<IConsistencyCheckHistory>> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/history`, {
		params,
		paramsSerializer: params => qs.stringify(params, { indices: false }),
	});
};

export const getConsistencyCheckHistoryStatistics = (
	dto: TConsistencyCheckHistoryStatisticsDTO,
): Promise<IItemsResponse<IConsistencyCheckHistoryStatistic>> => {
	return client.post(`${configurationConsistencyParametersAWTGRan}/statistics`, dto);
};

export const getConsistencyCheckHistoryNEInfo = ({ id }: IId): Promise<TConsistencyCheckHistoryInfo> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${id}/ne-info`);
};

export const getConsistencyCheckHistoryTemplates = ({ id }: IId): Promise<TIdName[]> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${id}/templates`);
};

export const getConsistencyCheckHistoryUsers = ({ id }: IId): Promise<TIdName[]> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${id}/users`);
};

export const getConsistencyCheckHistoryDetails = (
	dto: IConsistencyCheckHistoryDetailsDTO,
): Promise<IConsistencyCheckHistoryDetailResponse> => {
	return client.post(`${configurationConsistencyParametersAWTGRan}/details`, dto);
};

export const getConsistencyCheckHistoryConsistencyStatuses = (): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/static/consistency-statuses`);
};

export const getConsistencyCheckHistoryMonitoringStatuses = (): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/static/monitoring-statuses`);
};

export const getConsistencyCheckHistoryTriggerSources = (): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/static/trigger-sources`);
};

export const getConsistencyCheckHistorySummaryPathStatuses = (): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/static/path-statuses`);
};

export const getConsistencyCheckHistorySummary = (
	params: TConsistencyCheckHistoryStatisticsDTO,
): Promise<IItemsResponse<IConsistencyCheckHistorySummary>> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/summary`, { params });
};

export const getConsistencyCheckHistorySummaryInfo = ({ id }: IId): Promise<IConsistencyCheckHistoryInfoSummary> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${id}/summary-info`);
};

export const getConsistencyCheckHistoryChangedTypes = ({
	dumpConsistencyId,
	...params
}: TDumpConsistencyIdAndPath): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${dumpConsistencyId}/change-types`, { params });
};

export const getConsistencyCheckHistoryManagedObjectsIds = ({
	dumpConsistencyId,
	...params
}: TConsistencyCheckHistoryManagedIds): Promise<string[]> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${dumpConsistencyId}/managed-objects-ids`, {
		params,
	});
};

export const getConsistencyCheckHistoryManagedObjectsNames = ({
	dumpConsistencyId,
	...params
}: TDumpConsistencyIdAndPath): Promise<string[]> => {
	return client.get(`${configurationConsistencyParametersAWTGRan}/${dumpConsistencyId}/managed-objects-names`, {
		params,
	});
};

export const getConsistencyCheckExtraPathMoIds = ({
	dumpConsistencyId,
	...params
}: TConsistencyCheckHistoryExtraPathMoIdsDTO): Promise<string[]> => {
	return client.get(
		`${configurationConsistencyParametersAWTGRan}/${dumpConsistencyId}/extra-path/managed-objects-ids`,
		{ params },
	);
};

export const getConsistencyCheckExtraPathMoNames = ({
	dumpConsistencyId,
	...params
}: TConsistencyCheckHistoryExtraPathMoNamesDTO,
): Promise<string[]> => {
	return client.get(
		`${configurationConsistencyParametersAWTGRan}/${dumpConsistencyId}/extra-path/managed-objects-names`,
		{ params },
	);
};

export const getConsistencyCheckExtraPathDetails = (
	dto: IConsistencyCheckHistoryExtraPathMoNamesDTO,
): Promise<IConsistencyCheckExtraPathDetails> => {
	return client.post(`${configurationConsistencyParametersAWTGRan}/extra-path/details`, dto);
};

export const exportConsistencyHistory = (
	dto: TConsistencyCheckHistoryExportDTO,
): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/history/export`, dto);
};

